import { useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { KpiRow } from "@/components/KpiRow";
import { ParkingGrid } from "@/components/ParkingGrid";
import { GridLegend } from "@/components/GridLegend";
import { ActionPanel } from "@/components/ActionPanel";
import { ActivityLog } from "@/components/ActivityLog";
import { OwnerDirectory } from "@/components/OwnerDirectory";
import { CapacityDialog } from "@/components/CapacityDialog";
import { ZoneDialog } from "@/components/ZoneDialog";
import { UnitSearch } from "@/components/UnitSearch";
import { useLotData } from "@/hooks/useLotData";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { AuthButton } from "@/components/AuthButton";
import { PendingApprovalBanner } from "@/components/PendingApprovalBanner";
import ResetPassword from "@/pages/ResetPassword";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { SquareParking } from "lucide-react";

function Dashboard() {
  const { spaces, stats, logs, now, refresh } = useLotData();
  const { user } = useAuth();
  const [selected, setSelected] = useState(null);
  const isAdmin = user?.role === "admin";

  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-20 bg-[#0A0A0A] border-b border-[#262626]">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4">
            <img
              src="/firstmed-logo.png"
              alt="FirstMed Ambulance"
              data-testid="firstmed-logo"
              className="h-8 sm:h-10 w-auto shrink-0"
            />
            <span className="h-8 w-px bg-[#262626] hidden sm:block" />
            <div className="flex items-center gap-2">
              <SquareParking className="w-6 h-6 text-[#007AFF]" />
              <h1 className="text-xl sm:text-2xl font-black tracking-tighter whitespace-nowrap" data-testid="app-title">
                LOT CONTROL
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <UnitSearch spaces={spaces} onFound={setSelected} />
            {isAdmin && <ZoneDialog spaces={spaces} refresh={refresh} />}
            {isAdmin && <CapacityDialog total={stats.total} refresh={refresh} />}
            <AuthButton />
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-8 py-8">
        <PendingApprovalBanner />
        <KpiRow stats={stats} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flex flex-col gap-6">
            <ParkingGrid
              spaces={spaces}
              selected={selected}
              now={now}
              onSelect={(n) => setSelected(n === selected ? null : n)}
            />
            <GridLegend spaces={spaces} now={now} />
          </div>
          <div className="flex flex-col gap-6">
            <ActionPanel
              selected={selected}
              space={spaces.find((s) => s.number === selected)}
              now={now}
              refresh={refresh}
            />
            <OwnerDirectory spaces={spaces} onSelect={setSelected} />
            <ActivityLog logs={logs} />
          </div>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
        <AuthDialog />
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
